import { memo } from 'react'
import { FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { resetAll } from '@wwt/shared/utils'

import { assert } from '@/shared/lib/assert'
import * as Errors from '@/shared/lib/errors'
import { useToast } from '@/shared/lib/hooks/useToast'
import { Button } from '@/shared/ui/Button'

import { assertionErrorHandler } from '../lib/handlers/assertionErrorHandler'
import { clientMajorErrorHandler } from '../lib/handlers/clientMajorErrorHandler'
import { serverUnavailableErrorHandler } from '../lib/handlers/serverUnavailableErrorHandler'
import { serverValidationErrorHandler } from '../lib/handlers/serverValidationErrorHandler'
import { wwtErrorLog } from '../logger/wwtErrorLog'
import { ErrorHandlerResult } from '../types/ErrorHandlerResult'
import { ErrorModal } from './ErrorModal'

export const ErrorBoundaryFallback = memo(
	({ error, resetErrorBoundary }: FallbackProps) => {
		const { t } = useTranslation('errors')

		const { toast } = useToast()

		/**
		 * Show errors with toasts
		 */
		if (
			error instanceof Errors.ServerValidationError ||
			error instanceof Errors.ServerUnavailableError ||
			error instanceof Errors.ClientMajorError ||
			error instanceof Errors.AssertionError
		) {
			let data: ErrorHandlerResult | null = null

			if (error instanceof Errors.ServerValidationError) {
				data = serverValidationErrorHandler(error)
			}

			if (error instanceof Errors.ServerUnavailableError) {
				data = serverUnavailableErrorHandler(error)
			}

			if (error instanceof Errors.ClientMajorError) {
				data = clientMajorErrorHandler(error)
			}

			if (error instanceof Errors.AssertionError) {
				data = assertionErrorHandler(error)
			}

			assert(data !== null, 'Show toast data in error boundary is null')

			if (data.reset) resetErrorBoundary()

			toast({
				layer: 'modals',
				duration: Infinity,
				...data.toastInfo
			})

			return null
		}

		/**
		 * Show modal's
		 */
		if (error instanceof Errors.ClientCriticalError) {
			wwtErrorLog('Client critical error!', error)

			return (
				<ErrorModal
					title={t(`client.critical.${error.type}.title`)}
					description={t(`client.critical.${error.type}.description`)}
					TopLeftButton={
						<Button onClick={resetErrorBoundary}>
							{t('default.buttons.continue')}
						</Button>
					}
					TopRightButton={
						<Button onClick={resetAll}>{t('default.buttons.resetAll')}</Button>
					}
					BottomButton={
						<Button asChild>
							<Link
								to="/"
								reloadDocument
							>
								{t('default.buttons.action')}
							</Link>
						</Button>
					}
				/>
			)
		}

		wwtErrorLog('Unknown error', error)

		return (
			<ErrorModal
				title={t('default.title')}
				description={t('default.description')}
				TopLeftButton={
					<Button onClick={resetErrorBoundary}>
						{t('default.buttons.continue')}
					</Button>
				}
				TopRightButton={
					<Button onClick={resetAll}>{t('default.buttons.resetAll')}</Button>
				}
				BottomButton={
					<Button asChild>
						<Link
							to="/"
							reloadDocument
						>
							{t('default.buttons.action')}
						</Link>
					</Button>
				}
			/>
		)
	}
)

ErrorBoundaryFallback.displayName = 'ErrorBoundaryFallback'
